import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './BuildingSelectionMap.scss';
import { Trans } from 'react-i18next';

interface BuildingSelectionMapProps {
  location: { lat: number; lng: number };
  onBuildingSelect: (buildingId: string | null, buildingGeometry: any | null, userClickedCoordinates: { lat: number; lng: number } | null) => void;
  isVisible: boolean;
  isQueryingBuilding: boolean;
  address: string;
  buildingId: string;
  showBuildingSelectionMap: boolean;
  isAddressIncomplete: boolean;
  addressMismatchError: boolean;
  actionType: string;
  buildingAddress: string;
}

const BuildingSelectionMap: React.FC<BuildingSelectionMapProps> = ({
  location,
  onBuildingSelect,
  isVisible,
  isQueryingBuilding,
  address,
  buildingId,
  showBuildingSelectionMap,
  isAddressIncomplete,
  addressMismatchError,
  actionType,
  buildingAddress
}) => {
  // Keep a stable ref to the callback to avoid unnecessary effect reruns
  const onBuildingSelectRef = useRef(onBuildingSelect);
  onBuildingSelectRef.current = onBuildingSelect;
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(null);
  const [hoveredBuildingId, setHoveredBuildingId] = useState<string | null>(null);

  // Initialise the map **once** when the component becomes visible.
  useEffect(() => {
    if (!isVisible || mapRef.current) return; // already created or not visible
    if (!mapContainerRef.current || !location) return;

    // Initialize map
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN!;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: `mapbox://styles/jaakkocom/${process.env.REACT_APP_MAPBOX_STYLE_ID}`,
      center: [location.lng, location.lat],
      zoom: 18, // High zoom to see buildings clearly
    });

    // Add navigation controls
    mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Create a custom marker element for the address location
    const markerElement = document.createElement('div');
    markerElement.className = 'building-selection-marker';
    markerElement.innerHTML = `
      <svg width="24" height="36" viewBox="0 0 24 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24s12-15 12-24c0-6.627-5.373-12-12-12zm0 16c-2.206 0-4-1.794-4-4s1.794-4 4-4 4 1.794 4 4-1.794 4-4 4z" fill="#FF5A5F"/>
      </svg>
    `;

    // Add marker to map
    markerRef.current = new mapboxgl.Marker(markerElement, {
      offset: [0, -18]
    })
      .setLngLat([location.lng, location.lat])
      .addTo(mapRef.current);

    // Wait for map to load
    mapRef.current.on('load', () => {
      if (!mapRef.current) return;

      // Add a source for highlighting selected building
      mapRef.current.addSource('selected-building', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      // Add a layer for the selected building highlight
      mapRef.current.addLayer({
        id: 'selected-building-fill',
        type: 'fill',
        source: 'selected-building',
        paint: {
          'fill-color': '#8095FF',
          'fill-opacity': 0.4
        }
      });

      // Add a layer for the selected building outline
      mapRef.current.addLayer({
        id: 'selected-building-outline',
        type: 'line',
        source: 'selected-building',
        paint: {
          'line-color': '#8095FF',
          'line-width': 3
        }
      });

      // Change cursor on hover over buildings
      mapRef.current.on('mouseenter', ['building', 'test-new-building'], () => {
        if (mapRef.current) {
          mapRef.current.getCanvas().style.cursor = 'pointer';
        }
      });

      mapRef.current.on('mouseleave', ['building', 'test-new-building'], () => {
        if (mapRef.current) {
          mapRef.current.getCanvas().style.cursor = '';
        }
        setHoveredBuildingId(null);
      });

      // Handle hover effect
      mapRef.current.on('mousemove', ['building', 'test-new-building'], (e) => {
        if (e.features && e.features.length > 0) {
          const buildingId = e.features[0].id?.toString() || null;
          setHoveredBuildingId(buildingId);
        }
      });

      // Handle building click
      mapRef.current.on('click', ['building', 'test-new-building'], (e) => {
        if (!e.features || e.features.length === 0) return;
        console.log('e', e);
        console.log('e.features', e.features);
        const feature = e.features[0];
        const buildingId = feature.id?.toString() || null;
        const userClickedCoordinates = { lat: e.lngLat.lat, lng: e.lngLat.lng };

        // Attempt to gather **all** slices of the same building (same feature id) currently rendered
        let mergedCoordinates: number[][][] = [];
        if (mapRef.current && buildingId) {
          try {
            const slices = mapRef.current.queryRenderedFeatures({
              layers: ['building', 'test-new-building'],
              filter: ['==', ['id'], feature.id as any]
            });

            slices.forEach((slice) => {
              if (slice.geometry?.type === 'Polygon') {
                mergedCoordinates.push(...(slice.geometry.coordinates as number[][][]));
              } else if (slice.geometry?.type === 'MultiPolygon') {
                // flatten
                (slice.geometry.coordinates as number[][][][]).forEach((poly) => {
                  mergedCoordinates.push(...poly);
                });
              }
            });
          } catch (err) {
            console.warn('Could not queryRenderedFeatures for building slices', err);
          }
        }

        // Fallback to original feature geometry if we did not manage to merge
        if (mergedCoordinates.length === 0 && feature.geometry?.type === 'Polygon') {
          mergedCoordinates = feature.geometry.coordinates as number[][][];
        }

        // Use polygon-clipping to union slices and remove internal borders
        let buildingGeometry: any;
        try {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const pc = require('polygon-clipping');
          const unionResult: number[][][][] = pc.union(
            ...mergedCoordinates.map((ring) => [ring])
          );

          if (unionResult.length === 1) {
            buildingGeometry = { type: 'Polygon', coordinates: unionResult[0] };
          } else {
            buildingGeometry = { type: 'MultiPolygon', coordinates: unionResult };
          }
        } catch (err) {
          console.warn('polygon-clipping union failed, falling back to multi-polygon', err);
          buildingGeometry = mergedCoordinates.length > 1
            ? { type: 'MultiPolygon', coordinates: mergedCoordinates.map((ring) => [ring]) }
            : { type: 'Polygon', coordinates: mergedCoordinates };
        }

        setSelectedBuildingId(buildingId);

        // Update the selected building highlight
        if (mapRef.current) {
          const source = mapRef.current.getSource('selected-building') as mapboxgl.GeoJSONSource;
          source.setData({
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              geometry: buildingGeometry,
              properties: {}
            }]
          });
        }

        // Move marker to centroid of merged coordinates
        if (markerRef.current && mergedCoordinates.length > 0) {
          try {
            let sumLng = 0;
            let sumLat = 0;
            let count = 0;
            mergedCoordinates.forEach((ring) => {
              ring.forEach((coord) => {
                sumLng += coord[0];
                sumLat += coord[1];
                count += 1;
              });
            });
            if (count > 0) {
              const centroid = { lat: sumLat / count, lng: sumLng / count };
              markerRef.current.setLngLat([centroid.lng, centroid.lat]);
            }
          } catch (err) {
            console.error('Error moving marker to merged centroid', err);
          }
        }

        // Notify parent
        onBuildingSelectRef.current(buildingId, buildingGeometry, userClickedCoordinates);
      });

      // Handle click on empty space (deselect building)
      mapRef.current.on('click', (e) => {
        // Check if click was on a building
        const features = mapRef.current?.queryRenderedFeatures(e.point, {
          layers: ['building', 'test-new-building']
        });

        if (!features || features.length === 0) {
          // Clear selection
          setSelectedBuildingId(null);

          if (mapRef.current) {
            const source = mapRef.current.getSource('selected-building') as mapboxgl.GeoJSONSource;
            source.setData({
              type: 'FeatureCollection',
              features: []
            });
          }

          onBuildingSelectRef.current(null, null, null);
        }
      });
    });

    // Cleanup
    return () => {
      markerRef.current?.remove();
      mapRef.current?.remove();
    };
  }, [isVisible, location]);

  // Update hover effect
  useEffect(() => {
    if (!mapRef.current || !mapRef.current.isStyleLoaded()) return;

    // Reset any previous hover effects
    mapRef.current.setPaintProperty('building', 'fill-opacity', [
      'case',
      ['==', ['id'], hoveredBuildingId],
      0.8,
      0.5
    ]);

    mapRef.current.setPaintProperty('test-new-building', 'fill-opacity', [
      'case',
      ['==', ['id'], hoveredBuildingId],
      0.8,
      0.5
    ]);
  }, [hoveredBuildingId]);

  if (!isVisible) return null;

  return (
    <div className="building-selection-map-container">
      <div className="building-selection-map-instructions">
        <p>
          <Trans i18nKey="building.offMarket.modal.clickBuildingInstruction" />
        </p>
        {selectedBuildingId && (
          <p className="building-selection-map-selected">
            ✓ <Trans i18nKey="building.offMarket.modal.buildingSelected" />
          </p>
        )}
      </div>
      {isQueryingBuilding && (
        <div className="could-buy-or-could-sell-modal__building-status could-buy-or-could-sell-modal__building-status--loading">
          <Trans i18nKey="building.offMarket.modal.checkingBuilding" />
        </div>
      )}
      {!isQueryingBuilding && address && buildingId && !showBuildingSelectionMap && (
        <div className="could-buy-or-could-sell-modal__building-status could-buy-or-could-sell-modal__building-status--found">
          ✓ <Trans i18nKey="building.offMarket.modal.buildingFound" />
        </div>
      )}
      {!isQueryingBuilding && address && isAddressIncomplete && (
        <div className="could-buy-or-could-sell-modal__building-status could-buy-or-could-sell-modal__building-status--not-found">
          <Trans i18nKey="building.offMarket.modal.incompleteAddress" />
        </div>
      )}
      {!isQueryingBuilding && address && !buildingId && location && !isAddressIncomplete && !showBuildingSelectionMap && (
        <div className="could-buy-or-could-sell-modal__building-status could-buy-or-could-sell-modal__building-status--not-found">
          <Trans i18nKey={`building.offMarket.modal.${actionType}InterestNoBuildingFound`} />
        </div>
      )}
      {addressMismatchError && (
        <>
          <div className="could-buy-or-could-sell-modal__building-status could-buy-or-could-sell-modal__building-status--error">
            <Trans i18nKey="landingPage.couldBuyOrCouldSellModal.addressMismatch" />
          </div>
          <div className="building-selection-map-instructions">
            <p>
              <Trans i18nKey="building.offMarket.modal.receivedAddress" /> {buildingAddress}
            </p>
          </div>
        </>
      )}
      <div ref={mapContainerRef} className="building-selection-map" />
    </div>
  );
};

export default BuildingSelectionMap; 